import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, UrlTree } from "@angular/router";
import { combineLatest, Observable } from "rxjs";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { map, switchMap } from "rxjs/operators";
import { TransactionsFacade } from "@modules/+new/transactions/domain/+state/transactions.facade";
import { FiatTransaction } from "@modules/+new/transactions/domain/entities/fiat-transaction.model";
import { CheckoutDepositWebsocketStateService } from "@modules/checkout/services/checkout-deposit-websocket-state.service";
import { CheckoutDepositState } from "@modules/checkout/entities/checkout.deposit-state";
import { AmountType, CheckoutRouteName, CheckoutQueryParamName } from "@modules/checkout/entities/checkout.model";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import { CheckoutDepositData } from "@modules/checkout/entities/checkout.deposit-data";
import { DepositFlowType } from "@modules/checkout/entities/deposit-flow-type";
import { MT_QUERY_PARAMS } from "@helpers/constants";
import { MarginTradingRouteName } from "@modules/margin-trading/entities/margin-trading.model";

@Injectable({
	providedIn: "root"
})
export class OpenStatusModalGuard {
	private action: "success" | "fail" | "pending";

	constructor(
		private router: Router,
		private initService: InitService,
		private transactionFacade: TransactionsFacade,
		private checkoutDepositWebsocketStateService: CheckoutDepositWebsocketStateService,
		private checkoutNavigationService: CheckoutNavigationService
	) {}

	public canActivate(
		route: ActivatedRouteSnapshot
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		const { action, pid, fpid } = route.params;
		const { trade_flow, asset_id, asset_wallet_id } = route.queryParams;
		this.action = action;
		if (action === "cancel" || action === "fail") {
			this.action = "fail";
		} else {
			this.action = "success";
		}
		if (trade_flow === DepositFlowType.CHECKOUT_FLOW) {
			return this.initService
				.waitForInitServices([ServiceNames.MASTER_DATA, ServiceNames.INIT_ASSETS_SERVICE, ServiceNames.USER])
				.pipe(
					switchMap(() =>
						combineLatest([
							this.transactionFacade.getFiatTransaction$(pid, fpid),
							this.checkoutDepositWebsocketStateService.getStatusFromEndpoint(fpid)
						])
					),
					map(([tx, endpointStatus]) => {
						const checkoutDepositData: CheckoutDepositData = {
							fiatId: tx.fiatId,
							fiatWalletId: tx.fiatWalletId,
							amount: tx.amount,
							assetId: asset_id,
							walletId: asset_wallet_id,
							paymentMethodId: tx.paymentOptionId
						};
						if (
							checkoutDepositData &&
							(action === "cancel" ||
								action === "fail" ||
								endpointStatus === CheckoutDepositState.FAILURE)
						) {
							return this.checkoutNavigationService.gotoByUrlTree(
								CheckoutRouteName.CheckoutDepositPaymentFailed,
								{
									...checkoutDepositData
								}
							);
						}
						//later we do if (tx.status in CheckoutDepositState) to check for all states and handle them all
						if (endpointStatus === CheckoutDepositState.SUCCESS) {
							if (window.localStorage.getItem(MT_QUERY_PARAMS)) {
								const query = JSON.parse(window.localStorage.getItem(MT_QUERY_PARAMS));
								// use async navigation to remove local storage
								return this.checkoutNavigationService.gotoByUrlTreeFlexi(
									MarginTradingRouteName.MarginTradingBuySummary,
									query
								);
							}

							return this.checkoutNavigationService.gotoByUrlTree(CheckoutRouteName.Summary, {
								[CheckoutQueryParamName.AssetId]: checkoutDepositData.assetId,
								[CheckoutQueryParamName.BuyAmount]: checkoutDepositData.amount,
								[CheckoutQueryParamName.FiatId]: checkoutDepositData.fiatId,
								[CheckoutQueryParamName.FiatWalletId]: checkoutDepositData.fiatWalletId,
								[CheckoutQueryParamName.WalletGuid]: checkoutDepositData.walletId,
								[CheckoutQueryParamName.selectedAmountType]: AmountType.fiat
							});
						}
						if (endpointStatus === CheckoutDepositState.POTENTIAL_FRAUD) {
							return this.checkoutNavigationService.gotoByUrlTree(
								CheckoutRouteName.CheckoutDepositPaymentOnHold
							);
						}

						if (endpointStatus === CheckoutDepositState.MANUAL_APPROVAL && checkoutDepositData?.assetId) {
							return this.checkoutNavigationService.gotoByUrlTree(
								CheckoutRouteName.CheckoutDepositPaymentUnderReview,
								{ assetId: checkoutDepositData.assetId }
							);
						}
						this.checkoutDepositWebsocketStateService.initSocketService(checkoutDepositData);
						return this.checkoutDepositWebsocketStateService.showProcessingScreenByUrlTree();
					})
				);
		}
		const nav = this.router.currentNavigation();
		const currentUrl = nav.finalUrl.toString();
		const hasStatusAlready = currentUrl.match(`${ROUTER_OUTLET_NAME}:status`);
		if (action === TRANSACTION_STATUS.CANCEL) {
			this.action = TRANSACTION_STATUS.FAIL;
		} else {
			this.action = action;
		}
		if (hasStatusAlready) {
			return true;
		}
		return this.initService
			.waitForInitServices([ServiceNames.MASTER_DATA, ServiceNames.INIT_ASSETS_SERVICE, ServiceNames.USER])
			.pipe(
				switchMap(() => this.transactionFacade.getFiatTransaction$(pid, fpid)),
				map((tx: FiatTransaction) => {
					if (tx.type !== "deposit") {
						return true;
					}

					if (this.action === "success") {
						this.router.navigateByUrl(`${currentUrl}(${ROUTER_OUTLET_NAME}:status/pending)`);
						return true;
					}
					//for mpay and some other providers we get back some parameters with the url
					//we need to append the modal status before the params otherwise the modal is not appearing
					const splitByOptParams = currentUrl.split("?");
					if (splitByOptParams.length === 2) {
						this.router.navigateByUrl(
							`${splitByOptParams[0]}(${ROUTER_OUTLET_NAME}:status/${this.action})/${splitByOptParams[1]}`
						);
					} else {
						this.router.navigateByUrl(`${currentUrl}(${ROUTER_OUTLET_NAME}:status/${this.action})`);
					}
					return true;
				})
			);
	}

	private cleanMYQueryParamsLocalStorage(): void {
		if (window.localStorage.getItem(MT_QUERY_PARAMS)) {
			window.localStorage.removeItem(MT_QUERY_PARAMS);
		}
	}
}

enum TRANSACTION_STATUS {
	FAIL = "fail",
	CANCEL = "cancel",
	SUCCESS = "success"
}
